import debounce from 'lodash/debounce';
import { memo, useCallback, useEffect, useRef, useState } from 'react';

import { VALIDATION_DEBOUNCE_TIME } from '~/constants';
import useMount from '~/hooks/useMount';

import ClanNameInput from './ClanNameInput';
import ClanTagInput from './ClanTagInput';

import type { FinishCreationType } from '~/Actions/ActionClanCreate';
import type { IClanCreateStateFields } from '~/Reducers/ReducerClanCreate';
import type { Nullable } from '~/types/generics';

type ClanInputWrapperProps = {
  className?: string;
  error: Nullable<string>;
  field: keyof IClanCreateStateFields;
  isDisabled: boolean;
  isFocusOn?: boolean;
  isValidating: boolean;
  value: string;
  onChange: (field: keyof IClanCreateStateFields, value: string, error: Nullable<string>) => void;
  onValidateOnServer: (
    field: keyof IClanCreateStateFields,
    value: string,
    signal?: AbortSignal,
  ) => Promise<FinishCreationType>;
};

const ClanInputWrapper = ({
  className,
  error: propsError,
  field,
  isDisabled,
  isFocusOn,
  isValidating,
  value: propsValue,
  onChange,
  onValidateOnServer,
}: ClanInputWrapperProps) => {
  const [value, setValue] = useState(propsValue);
  const [error, setError] = useState(propsError);
  const [localError, setLocalError] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    if (propsError !== error && !localError) {
      setError(propsError);
    }
  }, [propsError, error, localError]);

  const onChangePurified = useCallback(
    (value: string, error: Nullable<string>) => {
      onChange(field, value, error);
    },
    [field, onChange],
  );

  const validateOnServerDebounce = useRef(
    debounce((value: string) => {
      abortControllerRef.current?.abort();
      const controller = new AbortController();
      abortControllerRef.current = controller;
      void onValidateOnServer(field, value, controller.signal);
    }, VALIDATION_DEBOUNCE_TIME),
  ).current;

  const handleChange = useCallback(
    (value: string, error: Nullable<string>) => {
      abortControllerRef.current?.abort();
      validateOnServerDebounce.cancel();
      setValue(value);
      setError(error);
      setLocalError(!!error);

      onChangePurified(value, error);
      if (error === null) {
        validateOnServerDebounce(value);
      }
    },
    [onChangePurified, validateOnServerDebounce],
  );

  useMount(() => {
    return () => {
      validateOnServerDebounce.cancel();
      abortControllerRef.current?.abort();
    };
  });

  const inputProps = {
    className,
    isDisabled,
    isFocusOn,
    isValidating,
    error,
    value,
    onChange: handleChange,
  };

  if (field === 'name') {
    return <ClanNameInput {...inputProps} />;
  }

  return <ClanTagInput {...inputProps} />;
};

export default memo(ClanInputWrapper);
